import { fetchDivisions } from '@/shared/api';
import {
  fetchRefreshShipmentTracking,
  handleRejectRmaTicket,
  handleUpdateRmaTicket,
  fetchRmaTicket,
} from '@/entities/rma';
import { useSupportStore } from '@/entities/support';
import { useLoadingStore } from '@/shared/model';
import { EMITTER_CONSTANTS, LOADING_TYPES } from '@/shared/config';
import {
  preventAppScroll,
  handleFetchCountries,
  showMessageError,
  showMessageSuccess,
} from '@/shared/lib';
import { isFacilitySubscribeRenewalStock } from '@/entities/facility';
import { useEmitter, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { isChrome, isFirefox } from 'react-device-detect';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { t } from 'ttag';

/**
 * Drives the RMA details page: ticket loading, tab state, scroll tracking, saving edits, cancellation and shipment tracking refreshes. Keeps merged state for the ticket and UI flags, reloads on the RMA_TICKET_UPDATED emitter and on route id changes, binds a window scroll listener, scrolls to the tracking section named by the router location state, and derives hasRenewalStock from the ticket's facility looked up in the support store.
 *
 * @returns The RMA details state together with its ref, navigator and tab, save, cancel and tracking-refresh handlers.
 */
const useRmaDetails = () => {
  const { id } = useParams();

  const { state: locationState } = useLocation();

  const navigate = useNavigate();

  /**
   * The support facility list read from the support Zustand store. Selects supportFacilities, the array the store keeps as a shallow copy of the last value set.
   *
   * @returns The support facility list held in the store.
   */
  const supportFacilities = useSupportStore((s) => s.supportFacilities);

  /**
   * The loading-text setter taken from the loading Zustand store. Selects setLoadingText, used to raise and clear the page-level saving indicator.
   *
   * @returns The store's setLoadingText action.
   */
  const setLoadingPage = useLoadingStore((s) => s.setLoadingText);

  const [state, setState] = useMergeState<any>({
    isScroll: false,
    isLoadedData: false,
    rmaTicket: undefined,
    hasChangedData: false,
    activeTab: '1',
    isOpenCancelRequestModal: false,
    isLoadingModal: false,
  });

  const newTicketRef = useRef<any>(undefined);

  /**
   * Loads everything the RMA details page renders: the ticket, the country list, the US and CA state divisions. Fires the four requests through Promise.all, reshapes each division list into label/value/fullCode options, commits them together with the ticket, and on failure logs and shows the error before marking the data loaded.
   *
   * @returns Resolves once the fetched data has been committed to state.
   */
  const fetchData = async () => {
    setState({ isLoadedData: false });
    try {
      const promises = [
        fetchRmaTicket(id),
        handleFetchCountries(),
        fetchDivisions('US'),
        fetchDivisions('CA'),
      ];
      const [rmaTicket, countries, stateCA, stateUS] = await Promise.all(promises);
      /**
       * Canadian province options derived from the fetched CA division list. Maps each division's name, code and fullCode into the label/value/fullCode shape consumed by the address select inputs.
       *
       * @returns Province options with label, value and fullCode.
       */
      const listStatesCA = _.map(stateCA, ({ name: label, code: value, fullCode }) => ({
        label,
        value,
        fullCode,
      }));
      /**
       * United States state options derived from the fetched US division list. Maps each division's name, code and fullCode into the label/value/fullCode shape consumed by the address select inputs.
       *
       * @returns State options with label, value and fullCode.
       */
      const listStatesUS = _.map(stateUS, ({ name: label, code: value, fullCode }) => ({
        label,
        value,
        fullCode,
      }));
      setState({
        rmaTicket,
        listCountries: countries,
        listStatesCA,
        listStatesUS,
      });
    } catch (error) {
      console.error('Failed to fetch data: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
    }
    setState({ isLoadedData: true });
  };

  /**
   * Rejects the current RMA ticket from the details page after persisting its clinic notes. Saves the notes through handleUpdateRmaTicket, rejects the ticket through handleRejectRmaTicket, writes the ticket back locally with status Rejected, and closes the confirmation modal whether or not the calls succeed.
   *
   * @returns Resolves once the cancellation has been applied and the modal closed.
   */
  const handleCancelRmaTicket = async () => {
    setState({ isLoadingModal: true });
    try {
      const clinicNotes = newTicketRef.current?.getClinicNotes();
      const updateRmaTicket = await handleUpdateRmaTicket({ id, clinicNotes });
      await handleRejectRmaTicket(id);
      const { rmaTicket } = updateRmaTicket;
      setState({
        rmaTicket: {
          ...rmaTicket,
          status: 'Rejected',
        },
      });
    } catch (error) {
      console.error('Failed to cancel RMA ticket: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
    }
    setState({ isLoadingModal: false, isOpenCancelRequestModal: false });
  };

  /**
   * Persists edited RMA ticket fields against the ticket id taken from the route. Merges the id into the payload, replaces the local ticket and shows a success message when the mutation reports isSuccess, otherwise surfaces the returned or thrown message as an error, then clears the page loading text.
   *
   * @param updateData - Changed RMA ticket fields to save.
   * @returns Resolves once the update result has been applied.
   */
  const handleUpdateData = async (updateData: any) => {
    try {
      const input = {
        id,
        ...updateData,
      };
      const result = await handleUpdateRmaTicket(input);
      if (result?.isSuccess) {
        setState({ rmaTicket: result.rmaTicket });
        showMessageSuccess({
          content: t`Saved successfully`,
        });
      } else {
        showMessageError({ content: result.message });
      }
    } catch (error) {
      showMessageError({ content: error instanceof Error ? error.message : '' });
    }
    setLoadingPage();
  };

  /**
   * Collects the RMA ticket form's pending changes and saves them. Raises the SAVING loading text, asks the form ref for its update payload, delegates to handleUpdateData when the payload is non-empty and otherwise just clears the loading text.
   *
   * @returns Resolves once the pending changes have been dispatched or the loading text cleared.
   */
  const validateUpdateData = async () => {
    setLoadingPage(LOADING_TYPES.SAVING);
    const updateData = await newTicketRef.current?.getUpdateData();
    if (!_.isEmpty(updateData)) {
      handleUpdateData(updateData);
    } else {
      setLoadingPage();
    }
  };

  /**
   * Records whether the RMA ticket form currently holds unsaved edits. Merges the given flag into the hook state as hasChangedData.
   *
   * @param hasChangedData - True when the form has pending edits.
   * @returns Nothing.
   */
  const updateHasChangedData = (hasChangedData: any) => {
    setState({ hasChangedData });
  };

  /**
   * Activates the requested tab of the RMA details page. Writes the tab into activeTab only when it differs from the current one, skipping a redundant state merge.
   *
   * @param tab - Identifier of the tab to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: any) => {
    if (tab !== state.activeTab) {
      setState({ activeTab: tab });
    }
  };

  /**
   * Refetches carrier tracking history for one shipment label of the RMA ticket and merges it into the displayed ticket. Clones the ticket, picks the sender labels for Outbound and the recipient labels otherwise, copies the fetched trackingHistory onto the label matching transactionId and back over the entry at index, surfaces failures through showMessageError, and always invokes callback when it is a function.
   *
   * @param type - Shipment direction, 'Outbound' for sender labels or anything else for recipient labels.
   * @param transactionId - Carrier transaction identifier of the label to refresh.
   * @param ticketId - Identifier of the RMA ticket owning the label.
   * @param index - Position of the label inside the direction's label list.
   * @param callback - Optional function invoked once the refresh settles.
   * @returns Resolves once the refreshed tracking has been merged into state.
   */
  const handleRefreshTracking = async ({ type, transactionId, ticketId, index, callback }: any) => {
    try {
      const tracking = await fetchRefreshShipmentTracking({
        transactionId,
        ticketId,
      });
      const clonedRmaTicket = _.cloneDeep(state.rmaTicket);
      /**
       * The shipping label list for the requested tracking direction. Reads senderInformation.labels for Outbound and recipientInformation.labels otherwise, both taken from the cloned RMA ticket.
       *
       * @returns The label list for the requested direction, possibly undefined.
       */
      const labels =
        type === 'Outbound'
          ? clonedRmaTicket?.senderInformation?.labels
          : clonedRmaTicket?.recipientInformation?.labels;
      /**
       * The shipping label whose transaction identifier matches the requested transaction. Located by a linear search over the cloned ticket's outbound or inbound label list.
       *
       * @returns The matching shipping label, or undefined when none matches.
       */
      const foundLabel = _.find(labels, (x) => x.transaction.id === transactionId);
      if (foundLabel) {
        foundLabel.trackingHistory = tracking?.trackingHistory;
      }
      _.assign(labels?.[index], { ...foundLabel });
      setState({ rmaTicket: clonedRmaTicket });
    } catch (error) {
      console.error('Failed to refresh tracking: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
    } finally {
      if (_.isFunction(callback)) {
        callback();
      }
    }
  };

  useEmitter(
    EMITTER_CONSTANTS.RMA_TICKET_UPDATED,
    (msg: any) => {
      if (msg?.id === id) {
        fetchData();
      }
    },
    [id],
  );

  useEffect(() => {
    document.title = t`Biocare Diagnostics - RMA Details`;
    preventAppScroll(false);
    fetchData();
  }, [id]);

  useEffect(() => {
    /**
     * Tracks whether the RMA details page has been scrolled away from the top so the sticky header can react. Reads window.scrollY and flips isScroll only when the stored flag disagrees with it.
     *
     * @returns Nothing.
     */
    const handleScroll = () => {
      if (window.scrollY === 0 && state.isScroll) {
        setState({ isScroll: false });
      } else if (window.scrollY !== 0 && !state.isScroll) {
        setState({ isScroll: true });
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [state.isScroll]);

  useUpdateEffect(() => {
    // Has scroll to prevent flicker and scroll again if click in notification in this page
    if (state.isLoadedData && !locationState?.hasScroll && locationState?.idScroll) {
      const activeTab = locationState.idScroll === 'outboundTrackingInformation' ? '1' : '2';
      setState({ activeTab });
      setTimeout(() => {
        const el = document.getElementById(locationState.idScroll);
        if (el) {
          if (isChrome || isFirefox) {
            window.scrollTo({
              top: el.getBoundingClientRect().top - 150,
              behavior: 'smooth',
            });
          } else {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
        _.assign(locationState, { hasScroll: true });
      }, 0);
    }
  }, [state.isLoadedData, locationState]);

  useUpdateEffect(() => {
    if (!locationState?.hasScroll && locationState?.idScroll) {
      fetchData();
    }
  }, [state.activeTab, locationState]);

  useEffect(() => {
    if (state.rmaTicket) {
      const { facility } = state.rmaTicket;
      if (facility) {
        /**
         * The support facility entry matching the RMA ticket's facility. Located by id within the support store's facility list so its programs can be checked for a renewal-stock subscription.
         *
         * @returns The matching support facility, or undefined when none matches.
         */
        const selectedFacilityWithEnabledProtectPlusField = _.find(
          supportFacilities,
          (x) => x.id === facility.id,
        );
        setState({
          hasRenewalStock: isFacilitySubscribeRenewalStock(
            selectedFacilityWithEnabledProtectPlusField?.programs,
          ),
        });
      }
    }
  }, [state.rmaTicket, supportFacilities]);

  return {
    state,
    setState,
    newTicketRef,
    navigate,
    handleCancelRmaTicket,
    validateUpdateData,
    updateHasChangedData,
    navigateTab,
    handleRefreshTracking,
  };
};

export { useRmaDetails };
